import React from "react";
import { withTranslation } from "react-i18next";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Spinner } from '../../../components/ui/Spinner';
import AppIcon from '../../../components/AppIcon';
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setPrevUrl } from "../../../store/redux/actions/userActionCreator";
import { Branch, Department, Roles, UserView } from "../../../models/general";
import { Roles as AuthRoles } from '../../../components/roles'
import { getBranches, getCompany, getDepartments } from "../../../api/General/Company";
import { deleteUser, getUser, restoreUser, FetchUsers } from "../../../api/General/account";
import Pagination from "../../../components/Pagination";
import { Users } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import ResetPasswordDialog from './ResetPasswordDialog';
import RestrictedWriteNotice from '../../../components/subscription/RestrictedWriteNotice';
import { RESTRICTED_WRITE_REASON_ID } from '../../../hooks/useWriteAccess';
import { withWriteAccess, type WithWriteAccessProps } from '../../../components/subscription/withWriteAccess';
import ManagedElsewhereNotice from '../../../components/capabilities/ManagedElsewhereNotice';
import { withCompanyCapabilities, type WithCompanyCapabilitiesProps } from '../../../components/capabilities/withCompanyCapabilities';

type Props = Readonly<{
    match?: any,
    t: any,
    setPrevUrl?: any,
    companyPrimaryColor?: any,
    companyName?: any,
    role?: any,
    company?: any,
    rtl?: boolean
    userId?: string,
    navigate?: (path: string) => void; // injected via withNavigation HOC
    // CO-24 — injected by withWriteAccess. /api/Account/AddUsers and /api/User are NOT on the
    // server's RestrictedWriteAllowlist, so every employee mutation 403s while Restricted/Dormant.
}> & WithWriteAccessProps & WithCompanyCapabilitiesProps;

type State = {
    data: any[];
    selectedPageIndex: number;
    count: number;
    openExpense: boolean;
    openBenefit: boolean;
    users: UserView[];
    allusers: UserView[];
    columns: any[];
    sortKey: 'fullName' | 'managerName' | 'title' | 'department' | null;
    sortDir: 'asc' | 'desc';
    showFilters: boolean;
    isArchivedChecked: boolean;
    loading: boolean;
    items: UserView[];
    offset: number;
    error?: string;
    companyName: string;
    roles: string[];
    branches: Branch[];
    departments: Department[];
    selectedRole: string;
    selectedBranch: string;
    selectedDepartment: string;
    selectedStatus: string;
    managers: any[];
    filter: {
        employeeId: string;
        fullName: string;
        email: string;
        company: string;
        branch: string;
        department: string;
        phoneNumber: string;
        country: string;
        role: string;
        positions: string[];
        isarchived: boolean
    };
    searchTerm: string;
    actionLoadingIds: string[];
    resetTarget: { id: string; name: string } | null;
};

class EmployeesGeneral extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            data: [],
            selectedPageIndex: 0,
            openExpense: false,
            openBenefit: false,
            offset: 0,
            count: 10,
            items: [],
            users: [],
            allusers: [],
            roles: [],
            branches: [],
            departments: [],
            loading: false,
            showFilters: false,
            isArchivedChecked: false,
            companyName: '',
            selectedRole: '',
            selectedBranch: '',
            selectedDepartment: '',
            selectedStatus: "",
            managers: [{
                userId: "",
                managerName: "",
            }],
            filter: {
                employeeId: "",
                fullName: "",
                email: "",
                company: "",
                branch: "",
                department: "",
                phoneNumber: "",
                country: "",
                role: "",
                positions: [],
                isarchived: false,
            },
            columns: [],
            sortKey: null,
            sortDir: 'asc',
            searchTerm: "",
            actionLoadingIds: [],
            resetTarget: null,
        };
    }

    toggleSort = (key: NonNullable<State['sortKey']>) => {
        this.setState(prev => {
            if (prev.sortKey === key) {
                return { sortKey: key, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' } as Pick<State, 'sortKey' | 'sortDir'>;
            }
            return { sortKey: key, sortDir: 'asc' } as Pick<State, 'sortKey' | 'sortDir'>;
        }, this.applyFilters);
    }
    currentURL = window.location.href;

    componentDidMount() {
        this.fetchData()
        console.log(this.currentURL);
        console.log("RTL OR LTR?: ", this.props.rtl ? this.props.rtl : this.props.rtl);
    }

    fetchData = async () => {
        const { t } = this.props
        const checked = this.state.isArchivedChecked
        this.setState({ loading: true });
        const id = this.props.company?.id ?? 0;
        if (id || AuthRoles.ADMIN.includes(this.props.role)) {
            try {
                if (id) {
                    const company = (await getCompany(id)).payload;
                    this.setState({ companyName: company.name })
                }
                // unified users fetch (Company scope). archived toggle handled via param
                const usersResult = await FetchUsers('Company', id, checked);
                const users = usersResult.payload;
                const roles = { payload: ["Clocking", "Chief", "Hr", "Admin"] };
                // Build managers mapping from payload (ManagerIds + ManagerNames aligned by index)
                const managers = users.map((u: any) => ({
                    userId: u.id,
                    managerName: (u.managerNames ?? [])
                }));
                this.setState({
                    loading: false,
                    users: users,
                    items: [],
                    roles: roles.payload,
                    managers: managers
                }, this.applyFilters);
                const branches = await getBranches(id)
                this.setState({ branches: branches.payload });
                const departments = await getDepartments(id)
                this.setState({ departments: departments.payload });
            } catch (error: any) {
                if (error && error.errors && error.errors.length > 0 && error.errors[0] && error.errors[0].description) {
                    alert(error.errors[0].description);
                }
                this.setState({ loading: false });
            }
        } else {
            this.setState({
                loading: false,
                error: t("User does not have a valid company"),
            })
        }
    };


    // Removed custom styled table components in favor of standardized table utility classes (.table-standard etc.)
    onPageChange = (index: number): void => {
        this.setState({
            selectedPageIndex: index,
            offset: (index ?? 1) * this.state.count
        })
    }
    filterDataGlobal(value: string) {
        this.setState({ searchTerm: value }, this.applyFilters);
    }

    updateFilter = (key: keyof State['filter'], value: string | boolean) => {
        this.setState(prev => ({ filter: { ...prev.filter, [key]: value } }), this.applyFilters);
    }

    toggleArchived = (checked: boolean) => {
        this.setState({ isArchivedChecked: checked }, () => {
            this.fetchData();
        });
    }

    clearFilters = () => {
        this.setState({
            filter: { employeeId: '', fullName: '', email: '', company: '', branch: '', department: '', phoneNumber: '', country: '', role: '', positions: [], isarchived: false },
            searchTerm: '',
            isArchivedChecked: false,
        }, this.applyFilters);
        this.fetchData();
    }

    applyFilters = () => {
        const { users, filter, searchTerm, isArchivedChecked } = this.state;
        const term = searchTerm.trim().toLowerCase();
        // When archived toggle is on, users list already contains only archived users (server filtered)
        let filtered = isArchivedChecked ? [...users] : users.filter(u => (u.isArchived || false) === isArchivedChecked);
        if (filter.role) filtered = filtered.filter(u => (u.role || '').toLowerCase() === filter.role.toLowerCase());
        if (filter.branch) filtered = filtered.filter(u => (u.branch?.id + '') === filter.branch);
        if (filter.department) filtered = filtered.filter(u => (u.department?.id + '') === filter.department);
        if (filter.positions.length > 0) filtered = filtered.filter(u => filter.positions.includes(u.title || ''));
        if (term) {
            filtered = filtered.filter(u => (
                u.userName?.toLowerCase().includes(term) ||
                u.employeeId?.toLowerCase().includes(term) ||
                u.company?.name?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.country?.name?.toLowerCase().includes(term) ||
                u.fullName?.toLowerCase().includes(term) ||
                u.phoneNumber?.toLowerCase().includes(term) ||
                u.role?.toLowerCase().includes(term) ||
                u.branch?.name?.toLowerCase().includes(term) ||
                u.department?.name?.toLowerCase().includes(term)
            ));
        }
        const { sortKey, sortDir, managers } = this.state;
        if (sortKey) {
            const getSortValue = (u: any): string => {
                switch (sortKey) {
                    case 'fullName': return (u.fullName || '').toLowerCase();
                    case 'title': return (u.title || '').toLowerCase();
                    case 'department': return (u.department?.name || '').toLowerCase();
                    case 'managerName': {
                        const mgr = managers.find(m => m.userId === u.id);
                        const names = mgr?.managerName;
                        return Array.isArray(names) ? names.join(', ').toLowerCase() : String(names || '').toLowerCase();
                    }
                }
                return '';
            };
            filtered = [...filtered].sort((a, b) => {
                const av = getSortValue(a);
                const bv = getSortValue(b);
                if (av < bv) return sortDir === 'asc' ? -1 : 1;
                if (av > bv) return sortDir === 'asc' ? 1 : -1;
                return 0;
            });
        }
        this.setState({
            items: filtered,
            selectedPageIndex: 0,
            offset: 0,
        });
    }


    render() {
        const checked = this.state.isArchivedChecked
        const { t } = this.props
        const visible = this.state.items;
        const total = this.state.users.length;
        const { loading, actionLoadingIds } = this.state as any;
        const isActionLoading = (id: string) => actionLoadingIds.includes(id);
        // Capability wins over write-access: a denial here is permanent, so the action is hidden rather than disabled.
        const canAuthorEmployees = this.props.companyCapabilities.loaded && this.props.companyCapabilities.can('canAuthorEmployees');
        return (
            <PageLayout
                title={t('Employees')}
                description={`${t('Showing')} ${visible.length} / ${total}`}
                icon={Users}
                accentColor="amber"
                maxWidth="1600px"
            >
                {loading && <div className="h-0.5 w-full bg-primary/20"><div className="h-full bg-primary animate-pulse w-1/2" /></div>}
                {/* CO-24 — restricted tenants cannot add, archive or restore employees. */}
                <RestrictedWriteNotice className="mb-4" />
                <ManagedElsewhereNotice capability="canAuthorEmployees" className="mb-4" />
                <section className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2 w-full">
                        <div className="relative flex-1">
                            <Input
                                placeholder={t('Search Employees')}
                                disabled={loading}
                                value={this.state.searchTerm}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.filterDataGlobal(e.target.value)}
                            />
                            <AppIcon name="Search" size={18} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                        <Button variant={this.state.showFilters ? 'default' : 'outline'} iconName="Filter" disabled={loading} onClick={() => this.setState(s => ({ showFilters: !s.showFilters }))}>{t('Filters')}</Button>
                        <Button variant="ghost" iconName="X" disabled={loading} onClick={this.clearFilters}>{t('Clear')}</Button>
                        {/*
                          🔴 CO-24 REPAIR — DO NOT GATE THE "Send Notification" BUTTON BELOW.
                          It navigates to the on-demand notification composer, which posts to
                          `/api/Notification/SendOnDemandNotification`. `/api/Notification` IS on
                          the server's RestrictedWriteAllowlist ("the bell still rings"), so the
                          write SUCCEEDS while Restricted/Dormant. The first CO-24 sweep disabled
                          it anyway — removing a capability the server honours, which is the exact
                          inversion CO-39 was fixed to prevent. Gate against the allowlist, never
                          against "it looks like a write".
                        */}
                        {(this.props.role === Roles.Admin || this.props.role === Roles.Hr) && (
                            <Button
                                disabled={loading}
                                variant="default"
                                iconName="Send"
                                onClick={() => {
                                    this.props.navigate?.(`${this.props.companyName ? '/' + this.props.companyName : ''}/users/send-notification`);
                                }}
                            >{t('Send Notification')}</Button>
                        )}
                        {canAuthorEmployees && (this.props.role === Roles.Admin || this.props.role === Roles.Hr) && (
                            <Button disabled={loading || this.props.writeAccess.blocked} {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} variant="outline" iconName="Plus" onClick={() => {
                                if (!canAuthorEmployees) return;
                                this.props.navigate?.(`${this.props.companyName ? '/' + this.props.companyName : ''}/users/add-multiple`);
                            }}>{t('Add multiple employees')}</Button>
                        )}
                        {canAuthorEmployees && (this.props.role === Roles.Admin || this.props.role === Roles.Hr) && (
                            <Button
                                disabled={loading || this.props.writeAccess.blocked}
                                {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})}
                                variant="default"
                                iconName="Plus"
                                onClick={() => {
                                    if (!canAuthorEmployees) return;
                                    this.props.navigate?.(`${this.props.companyName ? '/' + this.props.companyName : ''}/users/add`);
                                }}
                            >{t('Add employee')}</Button>
                        )}
                    </div>
                    {this.state.showFilters && (
                        <div className="section flex flex-wrap gap-4 p-2">
                            <Select
                                label={t('Role')}
                                options={[{ label: t('Any'), value: '' }, ...this.state.roles.map(r => ({ label: r, value: r.toLowerCase() }))]}
                                value={this.state.filter.role}
                                onChange={(value: string) => this.updateFilter('role', value)}
                                disabled={loading}
                                className="min-w-[150px]"
                            />
                            <Select
                                label={t('Branch')}
                                options={[{ label: t('Any'), value: '' }, ...this.state.branches.map(b => ({ label: b.name, value: String(b.id) }))]}
                                value={this.state.filter.branch}
                                onChange={(value: string) => this.updateFilter('branch', value)}
                                disabled={loading}
                                className="min-w-[170px]"
                            />
                            <Select
                                label={t('Department')}
                                options={[{ label: t('Any'), value: '' }, ...this.state.departments.map(d => ({ label: d.name, value: String(d.id) }))]}
                                value={this.state.filter.department}
                                onChange={(value: string) => this.updateFilter('department', value)}
                                disabled={loading}
                                className="min-w-[170px]"
                            />
                            <Select
                                label={t('Position')}
                                options={[...new Set(this.state.users.map(u => u.title).filter(Boolean))].sort().map(title => ({ label: title, value: title }))}
                                value={this.state.filter.positions}
                                onChange={(value: string[]) => this.setState(prev => ({ filter: { ...prev.filter, positions: value } }), this.applyFilters)}
                                multiple
                                searchable
                                clearable
                                placeholder={t('All Positions')}
                                disabled={loading}
                                className="min-w-[200px]"
                            />
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    role="switch"
                                    checked={this.state.isArchivedChecked}
                                    onChange={(e) => this.toggleArchived(e.target.checked)}
                                    disabled={loading}
                                    className="h-4 w-4 rounded border border-input"
                                />
                                <span className="text-sm text-foreground">{checked ? t('Archived') : t('Active')}</span>
                            </label>
                        </div>
                    )}
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> {t('Loading employees...')}</div>
                    )}
                    {!loading && visible.length === 0 && (
                        <div className="text-sm text-muted">{t('No employees found')}</div>
                    )}
                    <div className="table-container">
                        <Table className="table-standard table-outline-rows table-compact" aria-label={t('Employees table')}>
                            <TableHeader>
                                <TableRow>
                                    {(['fullName', 'managerName', 'title', 'department'] as const).map((key) => {
                                        const label = key === 'fullName' ? t('Employee Name')
                                            : key === 'managerName' ? t('Reporting To')
                                                : key === 'title' ? t('Position')
                                                    : t('Department');
                                        const isActive = this.state.sortKey === key;
                                        const indicator = isActive ? (this.state.sortDir === 'asc' ? '▲' : '▼') : '';
                                        return (
                                            <TableHead
                                                key={key}
                                                data-testid={`sort-${key}`}
                                                aria-sort={isActive ? (this.state.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                                role="columnheader"
                                                tabIndex={0}
                                                onClick={() => this.toggleSort(key)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggleSort(key); } }}
                                                className="cursor-pointer select-none hover:bg-accent"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    {label}
                                                    {indicator && <span aria-hidden="true" className="text-xs text-muted-foreground">{indicator}</span>}
                                                </span>
                                            </TableHead>
                                        );
                                    })}
                                    <TableHead>{t('Actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && Array.from({ length: 6 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}><div className="h-6 w-full animate-pulse rounded bg-muted" /></TableCell>
                                    </TableRow>
                                ))}
                                {!loading && visible.slice(this.state.offset, Math.min(this.state.offset + this.state.count, visible.length)).map((row) => (
                                    <TableRow key={row.fullName}>
                                        <TableCell>
                                            {(this.props.role === Roles.Hr || this.props.role === Roles.Admin) ? (
                                                <Link to={`/profile/${row.id}/personal`} onClick={() => { this.props.setPrevUrl(this.currentURL); }} className="text-primary font-semibold focus-ring">{row.fullName}</Link>
                                            ) : (<span className="font-semibold">{row.fullName}</span>)}
                                        </TableCell>
                                        <TableCell>{this.state.managers.filter(s => s.userId === row.id).map(s => s.managerName.join(', ')).join(', ')}</TableCell>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell>{row.department ? row.department.name : '-'}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1" dir={this.props.rtl ? 'rtl' : 'ltr'}>
                                                {(AuthRoles.MANAGER.includes(this.props.role)) && (
                                                    <span title={t('Attendance Overview')}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            disabled={loading}
                                                            asChild
                                                            aria-label={t('Attendance Overview')}
                                                        >
                                                            <Link to={`/attendance/overview/${row.id}${row.fullName ? `/${encodeURIComponent(row.fullName)}` : ''}`}>
                                                                <AppIcon name="Calendar" size={16} />
                                                            </Link>
                                                        </Button>
                                                    </span>
                                                )}
                                                {(AuthRoles.MANAGER.includes(this.props.role)) && (
                                                    <span title={t('Leaves & Holidays')}>
                                                        <Button variant="ghost" size="icon" disabled={loading} asChild aria-label={t('Leaves & Holidays')}>
                                                            <Link to={`/profile/${row.id}/leaves`}>
                                                                <AppIcon name="Palmtree" size={16} />
                                                            </Link>
                                                        </Button>
                                                    </span>
                                                )}
                                                {(this.props.role === Roles.Hr || this.props.role === Roles.Admin) && row.id !== this.props.userId && (
                                                    <span title={t('Reset password')}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            aria-label={t('Reset password')}
                                                            disabled={loading}
                                                            onClick={() => this.setState({ resetTarget: { id: row.id, name: row.fullName || row.email || row.id } })}
                                                        >
                                                            <AppIcon name="KeyRound" size={16} />
                                                        </Button>
                                                    </span>
                                                )}
                                                {canAuthorEmployees && (this.props.role === Roles.Hr || this.props.role === Roles.Admin) && (
                                                    checked ? (
                                                        <span title={t('Restore')}>
                                                            <Button variant="ghost" size="icon" aria-label={t('Restore')} {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} disabled={isActionLoading(row.id) || this.props.writeAccess.blocked} onClick={async () => {
                                                                if (!canAuthorEmployees) return;
                                                                if (window.confirm(t('Are you sure you want to restore this user'))) {
                                                                    this.setState((s: any) => ({ actionLoadingIds: [...s.actionLoadingIds, row.id] }));
                                                                    try { await restoreUser(row.id); await this.fetchData(); } finally { this.setState((s: any) => ({ actionLoadingIds: s.actionLoadingIds.filter((x: string) => x !== row.id) })); }
                                                                }
                                                            }}>
                                                                {isActionLoading(row.id) ? <Spinner size="sm" /> : <AppIcon name="Undo2" size={16} />}
                                                            </Button>
                                                        </span>
                                                    ) : (
                                                        <span title={t('Archive')}>
                                                            <Button variant="ghost" size="icon" aria-label={t('Archive')} {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} disabled={isActionLoading(row.id) || this.props.writeAccess.blocked} onClick={async () => {
                                                                if (!canAuthorEmployees) return;
                                                                if (window.confirm(t('Are you sure you want to archive this user'))) {
                                                                    this.setState((s: any) => ({ actionLoadingIds: [...s.actionLoadingIds, row.id] }));
                                                                    try { await deleteUser(row.id); await this.fetchData(); } finally { this.setState((s: any) => ({ actionLoadingIds: s.actionLoadingIds.filter((x: string) => x !== row.id) })); }
                                                                }
                                                            }}>
                                                                {isActionLoading(row.id) ? <Spinner size="sm" /> : <AppIcon name="Trash2" size={16} />}
                                                            </Button>
                                                        </span>
                                                    )
                                                )}
                                                {this.props.role === Roles.Admin && (
                                                    <span title={t('End Session')}>
                                                        <Button variant="ghost" size="icon" aria-label={t('End Session')} disabled={loading} onClick={async () => {
                                                            if (window.confirm(t('Are you sure you want to end the session for this user'))) {
                                                                //await revokeAccess(item.id);
                                                            }
                                                        }}>
                                                            <AppIcon name="LogOut" size={16} />
                                                        </Button>
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex justify-end pt-2">
                        <Pagination
                            currentPage={this.state.selectedPageIndex + 1}
                            totalPages={Math.ceil(visible.length / this.state.count) || 1}
                            onPageChange={(newPage) => {
                                const newPageIndex = newPage - 1;
                                this.setState({ selectedPageIndex: newPageIndex, offset: newPageIndex * this.state.count });
                            }}
                        />
                    </div>
                    {this.state.resetTarget && (
                        <ResetPasswordDialog
                            open={this.state.resetTarget !== null}
                            onOpenChange={(open) => { if (!open) this.setState({ resetTarget: null }); }}
                            userId={this.state.resetTarget.id}
                            userDisplayName={this.state.resetTarget.name}
                        />
                    )}
                </section>
            </PageLayout>
        );
    }
}

// HOC to inject navigate into class component
function withNavigation<P extends { navigate?: (path: string) => void }>(Component: React.ComponentType<P>) {
    return function Wrapped(props: Omit<P, 'navigate'>) {
        const navigate = useNavigate();
        return <Component {...props as P} navigate={(path: string) => navigate(path)} />;
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        setPrevUrl: (input: string) => dispatch(setPrevUrl(input))
    }
}
const mapStateToProps = (state: any) => {
    return {
        rtl: state.user.rtl,
        userId: state.user.userId,
        company: state.user.company,
        companyPrimaryColor: state.user.companyPrimaryColor,
        companyName: state.user.companyName,
        role: state.user.role,
    }
}
export default withCompanyCapabilities(withWriteAccess(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withNavigation(EmployeesGeneral) as any) as any) as any) as any);
